import type { ApiError } from './types/common.types';

const STATUS_MESSAGES: Record<number, string> = {
  400: 'The request was invalid. Please check your input and try again.',
  401: 'Your session has expired. Please log in again.',
  403: 'You are not authorised to perform this action.',
  404: 'The requested resource was not found.',
  409: 'A conflict occurred. The resource may already exist.',
  422: 'The submitted data could not be processed. Please check your input.',
  429: 'Too many requests. Please slow down and try again.',
  500: 'An internal server error occurred. Please try again later.',
  502: 'The server is temporarily unavailable. Please try again later.',
  503: 'The service is currently unavailable. Please try again later.',
};

const DEFAULT_MESSAGE = 'An unexpected error occurred. Please try again.';

/**
 * Resolves a human-readable message from an ApiError.
 * Uses the server-provided message when available, otherwise falls back
 * to a status-code-specific message, then the default.
 */
export function getErrorMessage(error: ApiError): string {
  // Always use the server message when available, except for 401 where the
  // session-expiry message is more user-friendly than raw token errors.
  if (error.status === 401 || !error.message) {
    return STATUS_MESSAGES[error.status] ?? DEFAULT_MESSAGE;
  }
  // For validation errors surface the first field-level message
  // (e.g. "End time must be after start time.") instead of the generic
  // "Validation failed." that the backend sends as the top-level message.
  if (error.errors) {
    const first = Object.values(error.errors).flat()[0];
    if (first) return first;
  }
  return error.message;
}

/**
 * Returns true if the error is an ApiError shape (has a numeric status).
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as ApiError).status === 'number'
  );
}

/**
 * Extracts a flat list of field-level validation messages from an ApiError.
 * Returns an empty array when there are no field errors.
 */
export function getFieldErrors(error: ApiError): Array<{ field: string; message: string }> {
  if (!error.errors) return [];
  return Object.entries(error.errors).flatMap(([field, messages]) =>
    messages.map((message) => ({ field, message })),
  );
}
